import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";
import { getEvaluationById, updateEvaluation } from "../../../api/evaluations";
import { useEffect } from "react";
import { Modal } from "react-bootstrap";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";

//Typage des props parents
type UUID = string;
type Props = {
  show: boolean;
  metaData: any;
  evaluationID: UUID;
  onClose: () => void;
  onSaved?: () => void;
};

enum NoteType {
  NoteClasse = "Note Classe",
  NoteExamen = "Note Examen",
}

enum Session {
  SessionPrincipale = "Session Principale",
  SessionRattrapage = "Session Rattrapage",
  SessionPayante = "Session Payante",
}

// shéma de notre formulaire
const createSchema = z.object({
  label: z.string().nonempty("Le libellé de la classe est requis"),
  abbreviation: z.string().nonempty("L'abréviation est requis"),
  typeNote: z.nativeEnum(NoteType, {
    errorMap: () => ({ message: "Veuillez choisir un type valide;" }),
  }),
  session: z.nativeEnum(Session, {
    errorMap: () => ({ message: "Veuillez choisir un type valide;" }),
  }),
});

type FormFields = z.infer<typeof createSchema>;

const EditEvaluationModal = ({
  metaData,
  evaluationID,
  show,
  onClose,
  onSaved,
}: Props) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      // console.log("formData: ", data);
      await updateEvaluation(evaluationID, metaData?.id_level, data);
      onClose(); // on ferme le modal
      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Evaluation mise à jour !");
    } catch (error: any) {
      // console.log("erreur modification d'une évaluation: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    if (!show || !evaluationID) return;
    const fetchEvaluationData = async () => {
      try {
        const response = await getEvaluationById(
          evaluationID,
          buildPermissionContext(Resource.EVALUATION, Action.UPDATE),
        );
        if (!response) throw new Error("Evaluation introuvable");
        // console.log("response: ", response.data);
        reset({
          label: response.data?.label,
          abbreviation: response.data?.abbreviation,
          typeNote: response.data?.typeNote,
          session: response.data?.session,
        });
      } catch (error: any) {
        // console.log("erreur récupération d'une évaluation: ", error);
        toast.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            "Une erreur s'est produit, Réessayer plus tard",
        );
      }
    };
    fetchEvaluationData();
  }, [show, evaluationID, reset]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Modification d'une Evaluation</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <form
            id="evaluation-form-edit"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Libellé
                  </label>
                  <input
                    {...register("label")}
                    type="text"
                    className={
                      errors.label ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: Evaluation 1 ou Evaluation 2"
                  />
                  {errors.label && (
                    <div className="invalid-feedback">
                      {errors.label.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Abréviation
                  </label>
                  <input
                    {...register("abbreviation")}
                    type="text"
                    className={
                      errors.abbreviation
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: Ev1 ou Ev2"
                  />
                  {errors.abbreviation && (
                    <div className="invalid-feedback">
                      {errors.abbreviation.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Type note</label>
                  <select
                    className={`form-select ${
                      errors.typeNote ? "is-invalid" : ""
                    }`}
                    {...register("typeNote")}
                  >
                    <option value="">Sélectionner le type</option>
                    {Object.entries(NoteType).map(([key, value]) => (
                      <option key={key} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  {errors.typeNote && (
                    <div className="invalid-feedback">
                      {errors.typeNote.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Type session</label>
                  <select
                    className={`form-select ${
                      errors.session ? "is-invalid" : ""
                    }`}
                    {...register("session")}
                  >
                    <option value="">Sélectionner session</option>
                    {Object.entries(Session).map(([key, value]) => (
                      <option key={key} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  {errors.session && (
                    <div className="invalid-feedback">
                      {errors.session.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>
          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="evaluation-form-edit" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  EditEvaluationModal,
  [buildPermission(Resource.EVALUATION, Action.UPDATE)],
  "masquer",
);
